<template>
  <div class="condition-report-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :search="tableData.search" :fields="tableFields"
                   :hide-per-page-selector="hidePerPageSelector" requires-min-height
                   :enable-simple-search="false" :index="false"
                   @paramsChanged="getTableData" :filter-toggle-string="filterToggleString" :extra-data="extraData">
      <template v-slot:header-buttons="{selectedItems}" v-if="!hideTopActions">
        <b-dropdown class="btn-table-actions" :text="$t('Actions')" variant="primary"
                    v-permission="'change_inventoryitem'">
          <b-dropdown-item v-if="$hasPermission('change_conditionreport')"
                           :disabled="tableData.loading"
                           @click="onNewConditionReportClick">
            {{ $t("New Condition Report") }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="condition-report-table">
          <condition-report-search :update-search-model="updateSearchModel" :get-table-data="getTableData"
                                   :vuex-search-model="tableData.searchModel"
                                   :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template slot="cell(title)" slot-scope="{item}">
        {{ item.title }}
        <action-buttons
            :edit-permission="'change_conditionreport'"
            :delete-permission="'delete_conditionreport'"
            :view="false"
            :edit="!hideAction(ACTION_EDIT_CONDITION_REPORT)"
            :remove="!hideAction(ACTION_REMOVE_CONDITION_REPORT)"
            @editClick="onEditClick(item)"
            @deleteClick="onRemoveClick(item)"/>
      </template>
      <template slot="cell(version)" slot-scope="{item}">
        {{ (item.version ? item.version : '') + (item.version_suffix ? item.version_suffix : '') }}
      </template>
      <template slot="cell(internal_note)" slot-scope="{item}">
        {{ item.internal_note | striptags | truncate(50) }}
      </template>
      <template slot="cell(status)" slot-scope="{item}">
        <span v-as-status="item.status" v-if="item.status">
          {{ item.status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
      </template>
      <template slot="cell(publish_on_website)" slot-scope="{item}">
        <span class="badge bg-success text-white" v-if="item.publish_on_website">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>
      <template slot="cell(created_at)" slot-scope="{item}">
        {{ item.created_at | toDateTime }}
      </template>
      <template slot="cell(created_by)" slot-scope="{item}">
        {{ item.created_by ? item.created_by.display_name : '' }}
      </template>
      <template slot="cell(released_at)" slot-scope="{item}">
        {{ item.released_at | toDateTime }}
      </template>
      <template slot="cell(released_by)" slot-scope="{item}">
        {{ item.released_by ? item.released_by.display_name : '' }}
      </template>
      <template slot="cell(actions)" slot-scope="{item}">
        <b-dropdown right class="text-white" variant="primary" no-caret size="sm"
                    v-if="!smallRowActions">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v" class="icon"/>
          </template>
          <b-dd-item @click="viewAsPdf(item)" v-if="item.status === 'released' && !hideAction('view_pdf')">
            <font-awesome-icon icon="file-pdf" style="font-size: .9em" class="mr-1"/>
            {{ $t('View as PDF') }}
          </b-dd-item>
          <b-dd-item @click="viewConditionImages(item)" v-if="!hideAction('condition_images')">
            <font-awesome-icon icon="file-alt" style="font-size: .9em" class="mr-1"/>
            {{ $t('Condition images') }}
          </b-dd-item>
          <b-dd-item v-permission="'change_conditionreport'" @click="useAsDraft(item)" v-if="item.status === 'released' && !hideAction('use_as_draft')">
            <font-awesome-icon icon="recycle" style="font-size: .9em" class="mr-1"/>
            {{ $t('Use as Draft') }}
          </b-dd-item>
          <b-dd-item @click="viewActivityLog(item)" v-if="!hideAction('view_activity_log')">
            <font-awesome-icon icon="history" style="font-size: .9em" class="mr-1"/>
            {{ $t('Activity Log') }}
          </b-dd-item>
          <b-dd-item @click="send(item)"
                     v-if="$hasPermission('export_conditionreport') && item.status === 'released' && !hideAction('send')">
            <font-awesome-icon icon="paper-plane" style="font-size: .9em" class="mr-1"/>
            {{ $t('Send') }}
          </b-dd-item>
        </b-dropdown>
        <template v-else>
          <b-button variant="primary" size="sm" @click="viewAsPdf(item)"
                    v-if="item.status === 'released' && !hideAction('view_pdf')"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('View as PDF')">
            <font-awesome-icon icon="file-pdf" class="icon"/>
          </b-button>
          <b-button variant="primary" size="sm" @click="viewConditionImages(item)"
                    v-if="!hideAction('condition_images')"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('Condition images')">
            <font-awesome-icon icon="file-pdf" class="icon"/>
          </b-button>
          <b-button variant="primary" size="sm" @click="useAsDraft(item)"
                    v-if="item.status === 'released' && !hideAction('use_as_draft')"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('Use as Draft')">
            <font-awesome-icon icon="recycle" class="icon"/>
          </b-button>
          <b-button variant="primary" size="sm" @click="viewActivityLog(item)" v-if="!hideAction('view_activity_log')"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('Activity Log')">
            <font-awesome-icon icon="history" class="icon"/>
          </b-button>
          <b-button variant="primary" size="sm" @click="send(item)"
                    v-if="$hasPermission('export_conditionreport') && item.status === 'released' && !hideAction('send')"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('Send')">
            <font-awesome-icon icon="paper-plane" class="icon"/>
          </b-button>
        </template>
      </template>
    </artisio-table>
    <condition-report-images-modal/>
    <condition-report-activities-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import i18n from "../../shared/i18n";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import {
    ACTION_CREATE_NEW_CONDITION_REPORT, ACTION_EDIT_CONDITION_REPORT, ACTION_REMOVE_CONDITION_REPORT,
    CONDITION_REPORTS_TABLE_NAME, SOURCE_CONDITION_REPORTS_IMAGES_MODAL, SOURCE_CONDITION_REPORTS_TABLE
  } from "@/modules/constants";
  import ConditionReportSearch from "@/modules/condition-reports/ConditionReportSearch.vue";
  import ActionButtons from "@/core/components/ams-table/ActionButtons.vue";
  import {ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import ConditionReportActivitiesModal from "@/modules/condition-reports/ConditionReportActivitiesModal.vue";
  import ConditionReportImagesModal from "@/modules/condition-report-images/ConditionReportImagesModal.vue";

  const {mapActions, mapState} = createNamespacedHelpers('conditionReports');
  const {mapActions: activitiesMapActions} = createNamespacedHelpers('activities');
  const {mapActions: requestsMapActions} = createNamespacedHelpers('conditionReportRequests');
  const {mapActions: conditionReportsMapActions} = createNamespacedHelpers('conditionReports');
  const {mapActions: conditionReportsImagesMapActions} = createNamespacedHelpers('conditionReportImages');

  export default {
    name: "ConditionReportsTable",
    components: {
      ConditionReportActivitiesModal, ConditionReportImagesModal, ActionButtons, ConditionReportSearch, SearchCollapse, ArtisioTable},
    props: {
      excludeColumns: {
        type: Array,
        default: () => [],
      },
      excludeSearchFields: {
        type: Array,
        default: () => [],
      },
      hidePerPageSelector: {
        type: Boolean,
        default: false
      },
      hideTopActions: {
        type: Boolean,
        default: false
      },
      smallRowActions: {
        type: Boolean,
        default: false
      },
      inventory: {
        type: Object,
        default: null
      },
      extraFilters: {
        type: Object,
        default: null
      },
      hideActions: {
        type: [Boolean, Array],
        default: () => []
      },
      source: {
        type: String,
        default: 'inventory'
      },
      filterToggleString: {
        type: String,
        default: 'condition-report-table'
      },
      sendOverride: {
        type: Function,
        default: null
      },
    },
    data() {
      return {
        ACTION_CREATE_NEW_CONDITION_REPORT: ACTION_CREATE_NEW_CONDITION_REPORT,
        ACTION_EDIT_CONDITION_REPORT: ACTION_EDIT_CONDITION_REPORT,
        ACTION_REMOVE_CONDITION_REPORT: ACTION_REMOVE_CONDITION_REPORT,
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
      }),
      tableFields() {
        return [
          {key: "title", label: i18n.t('Title'), sortable: true,},
          {key: "version", label: i18n.t('Version'), sortable: true,},
          {key: "internal_note", label: i18n.t('Internal Note'), sortable: true,},
          {key: "status", label: i18n.t('Status'), sortable: true,},
          {key: "publish_on_website", label: i18n.t('Published on Website'), sortable: true,},
          {key: "created_at", label: i18n.t('Created at'), sortable: true,},
          {key: "created_by", label: i18n.t('Created by'), sortable: true,},
          {key: "released_at", label: i18n.t('Released at'), sortable: true,},
          {key: "released_by", label: i18n.t('Released by'), sortable: true,},
          {key: "actions", label: i18n.t('Actions'), sortable: true,},
        ].filter(field => !this.excludeColumns.includes(field.key));
      },
      extraData() {
        return {
          tableName: CONDITION_REPORTS_TABLE_NAME
        }
      }
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'createConditionReport', 'removeConditionReport', 'updateSearchModel', 'updateTableSource', 'updateConditionReportPartially']),
      ...activitiesMapActions(['showActivitiesModal', 'setActivitiesParams']),
      ...conditionReportsMapActions(['showConditionReportImageModal']),
      ...requestsMapActions(['showSendModal']),
      ...conditionReportsImagesMapActions(['getConditionReportImages','showConditionReportImageModal']),
      hideAction(actionName) {
        return this.hideActions.includes(actionName)
      },
      onNewConditionReportClick() {
        this.showModal({
          object: {
            inventory: this.inventory,
            inventory_uuid: this.inventory.uuid
          },
          source: SOURCE_CONDITION_REPORTS_TABLE
        })
      },
      async onEditClick(item) {
        this.showModal({
          object: {
            ...item,
            inventory: this.inventory
          },
          source: SOURCE_CONDITION_REPORTS_TABLE
        });
      },
      async onRemoveClick(item) {
        this.$confirm(
            this.$i18n.t("Are you sure you want to delete following condition report?"),
            this.$i18n.t('Condition Report was successfully deleted'),
            this.removeConditionReport,
            item
        );
      },
      viewAsPdf(item) {
        const conditionReportName = item.title;

        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: conditionReportName,
        })

        this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
          ...item,
          filename: conditionReportName
        })
      },
      async useAsDraft(report) {
        const item = {...report}
        item.uuid = null;
        item.status = 'draft';
        item.version = null;
        this.showModal({
          object: {
            ...item,
            inventory: {...this.inventory}
          },
          source: SOURCE_CONDITION_REPORTS_TABLE
        });
      },
      viewActivityLog(item) {
        this.setActivitiesParams({object: item, source: 'condition_report', limit: 9});
        this.showActivitiesModal();
      },
      async viewConditionImages(item){
        this.showConditionReportImageModal({object: item, source: SOURCE_CONDITION_REPORTS_IMAGES_MODAL});
        await this.getConditionReportImages({...item})
      },
      send(item) {
        if (this.sendOverride) {
          return this.sendOverride(item);
        }
        this.showSendModal({
          object: {request_uuids: [], inventory_uuid: this.inventory.uuid, items: [], report: item},
          source: 'reports'
        });
      },
    },
    mounted() {
      this.getTableData();
    },
    beforeMount() {
      let extraFilters = {};
      if (this.inventory) {
        extraFilters.inventory_uuid = this.inventory.uuid
      }
      if (this.extraFilters) {
        extraFilters = {...extraFilters, ...this.extraFilters};
      }
      this.updateTableSource({source: this.source, extraFilters});
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";
</style>

